import React from 'react';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '../../../../components';
import type { StatsTopTable } from '../../../../mock-data/types';
import { LiveNumber } from '../LiveNumber';
import {
  Grid,
  Card,
  Header,
  HeadPrimary,
  HeadSecondary,
  Rows,
  Row,
  Rank,
  Name,
  Value,
  Empty,
} from './Spotlight.styles';

export type SpotlightProps = {
  tables: StatsTopTable[];
  /** Fallback loading for a table that carries no own `loading` (the still-mock board). */
  loading?: boolean;
};

/** Skeleton rows shown on a board's first load (before any rows are cached). */
const SKELETON_ROWS = 5;

/** "Spotlight" — the Top Games / Top Players leaderboards. Rows are pre-sorted
 *  descending; the stake figure tweens on each tick. The rank + name are static
 *  chrome; only the figure loads in. The two boards have independent sources (Top
 *  Players is API-wired, Top Games is still mock), so each loads on its own state:
 *  skeleton rows on first load, an empty/error line when a resolved board has no
 *  rows. */
export const Spotlight = ({ tables, loading = false }: SpotlightProps): React.ReactElement => {
  const { t } = useTranslation();
  // The board headings are localized here at the render site against the stable board
  // `id` so they re-evaluate on a language switch.
  const primaryLabel = (table: StatsTopTable): string =>
    table.id === 'top-games' ? t('Top Games') : t('Top Players');
  return (
    <Grid $cols={Math.max(tables.length, 1)}>
      {tables.map((table) => {
        const cardLoading = table.loading ?? loading;
        const hasRows = table.rows.length > 0;
        return (
          <Card key={table.id}>
            <Header>
              <HeadPrimary text={primaryLabel(table)} tag="span" />
              <HeadSecondary text={t('Stakes')} tag="span" />
            </Header>
            <Rows>
              {hasRows &&
                table.rows.map((r, i) => (
                  <Row key={`${table.id}-${i}`}>
                    <Rank>{i + 1}</Rank>
                    <Name text={r.name} tag="span" />
                    <Value>
                      <LiveNumber value={r.value} prefix="$" loading={cardLoading} />
                    </Value>
                  </Row>
                ))}
              {!hasRows &&
                cardLoading &&
                Array.from({ length: SKELETON_ROWS }, (_, i) => (
                  <Row key={`${table.id}-skeleton-${i}`}>
                    <Rank>{i + 1}</Rank>
                    <Skeleton block width="65%" height="0.9em" radius={5} />
                    <Value>
                      <Skeleton width="5ch" height="0.8em" radius={5} />
                    </Value>
                  </Row>
                ))}
              {!hasRows && !cardLoading && (
                <Empty text={table.error ? t('Could not load players') : t('No player activity yet')} tag="span" />
              )}
            </Rows>
          </Card>
        );
      })}
    </Grid>
  );
};

export default Spotlight;
